import OneSignal from 'react-onesignal';

import i18n from '../i18n/config';
import { getErrorMessage } from '../utils/errors';

export class OneSignalService {
  static isInitialized = false;
  static initError = false;
  static initializationPromise: Promise<void> | null = null;

  static async init() {
    try {
      if (window.location.hostname !== import.meta.env.VITE_APP_HOSTNAME) {
        this.initError = true;
        return;
      }

      await OneSignal.init({
        appId: import.meta.env.VITE_ONE_SIGNAL_APP_ID,
        allowLocalhostAsSecureOrigin: true,
      });
      this.isInitialized = true;
      console.log('OneSignal initialized');
    } catch (e) {
      this.initError = true;
      const errorMessage = getErrorMessage(e);
      console.error(`OneSignal initialization failed: ${errorMessage}`);
    }
  }

  static waitForInit() {
    if (this.isInitialized || this.initError) {
      return Promise.resolve();
    }

    if (this.initializationPromise !== null) {
      return this.initializationPromise;
    }

    this.initializationPromise = new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (this.isInitialized || this.initError) {
          resolve();
          clearInterval(interval);
        }
      }, 500);
    });

    return this.initializationPromise;
  }

  static async login(firebaseUid: string) {
    await this.waitForInit();
    if (this.isInitialized) {
      await OneSignal.login(firebaseUid);
    }
  }

  static async logout() {
    await this.waitForInit();
    if (this.isInitialized) {
      void OneSignal.logout();
    }
  }

  private static customizePrompt() {
    const actionMessage = i18n.t('pushNotifications.subscribeToNotifications');
    const acceptButton = i18n.t('pushNotifications.subscribeButton');
    const cancelButton = i18n.t('pushNotifications.laterButton');

    const slideDownContainer = document.getElementById('onesignal-slidedown-container');
    if (slideDownContainer) {
      slideDownContainer.style.pointerEvents = 'auto';

      const messageElement = slideDownContainer.querySelector('.slidedown-body-message');
      if (messageElement && actionMessage) {
        messageElement.textContent = actionMessage;
      }

      const acceptBtn = slideDownContainer.querySelector('.slidedown-button.primary');
      if (acceptBtn && acceptButton) {
        acceptBtn.textContent = acceptButton;
      }

      const cancelBtn = slideDownContainer.querySelector('.slidedown-button.secondary');
      if (cancelBtn && cancelButton) {
        cancelBtn.textContent = cancelButton;
      }
    }
  }

  static async promptPush() {
    await this.waitForInit();
    if (this.isInitialized) {
      OneSignal.Slidedown.removeEventListener('slidedownShown', this.customizePrompt.bind(this));
      // We block the UI when we open different dialogs, so when the notification prompt is shown, we need to remove the block
      OneSignal.Slidedown.addEventListener('slidedownShown', this.customizePrompt.bind(this));

      await OneSignal.Slidedown.promptPush();
    }
  }
}
